import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import complianceService from '../../services/complianceService';

// Import all components
import ComplianceOverviewCard from './components/ComplianceOverviewCard';
import ReportGenerationTool from './components/ReportGenerationTool';
import ComplianceCalendar from './components/ComplianceCalendar';
import DocumentManagement from './components/DocumentManagement';
import RiskAssessmentDashboard from './components/RiskAssessmentDashboard';
import NotificationSettings from './components/NotificationSettings';

const ComplianceStatutoryReports = () => {
  const { user, userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [complianceData, setComplianceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    compliant: 0,
    pending: 0,
    overdue: 0,
    non_compliant: 0
  });

  // Map report types to icons
  const getIconForReportType = (reportType) => {
    const iconMap = {
      'PF': 'Banknote',
      'ESI': 'Heart',
      'Professional Tax': 'FileText',
      'Labour Welfare Fund': 'Users',
      'Gratuity': 'Gift',
      'Bonus': 'DollarSign',
      'default': 'FileText'
    };
    return iconMap[reportType] || iconMap['default'];
  };

  // Transform database compliance report to component format
  const transformComplianceReport = (report) => {
    const statusMap = {
      'compliant': 'compliant',
      'pending': 'attention',
      'overdue': 'overdue',
      'non_compliant': 'overdue'
    };

    return {
      id: report.id,
      title: report.report_name || report.report_type,
      status: statusMap[report.status] || 'attention',
      lastUpdated: report.filing_date 
        ? new Date(report.filing_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : report.updated_at 
          ? new Date(report.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : 'N/A',
      dueDate: report.due_date 
        ? new Date(report.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : 'N/A',
      description: report.notes || `${report.report_type} compliance and reporting`,
      icon: getIconForReportType(report.report_type),
      reportType: report.report_type,
      assignedTo: report.assigned_name || 'Unassigned',
      documentUrl: report.document_url
    };
  };

  // Load compliance reports
  const loadComplianceReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await complianceService.getComplianceReports();
      
      if (result.success && result.data) {
        const transformed = result.data.map(transformComplianceReport);
        setComplianceData(transformed);

        // Calculate stats
        const statusCounts = {
          compliant: 0,
          pending: 0,
          overdue: 0,
          non_compliant: 0
        };

        result.data.forEach(report => {
          const status = report.status || 'pending';
          if (statusCounts.hasOwnProperty(status)) {
            statusCounts[status]++;
          }
        });

        setStats(statusCounts);
      } else {
        throw new Error(result.error || 'Failed to load compliance reports');
      }
    } catch (err) {
      console.error('Error loading compliance reports:', err);
      setError(err.message || 'Failed to load compliance reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplianceReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleViewDetails = (complianceId) => {
    console.log(`Viewing details for compliance item: ${complianceId}`);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'reports', label: 'Report Generation', icon: 'FileText' },
    { id: 'calendar', label: 'Compliance Calendar', icon: 'Calendar' },
    { id: 'documents', label: 'Document Management', icon: 'FolderOpen' },
    { id: 'risks', label: 'Risk Assessment', icon: 'AlertTriangle' },
    { id: 'notifications', label: 'Notification Settings', icon: 'Bell' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Compliance Status Summary */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Compliance Status Overview</h2>
                  <p className="text-muted-foreground">Current status of statutory compliance requirements</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success">{stats.compliant}</div>
                    <div className="text-xs text-muted-foreground">Compliant</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-warning">{stats.pending}</div>
                    <div className="text-xs text-muted-foreground">Pending</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-error">{stats.overdue + stats.non_compliant}</div>
                    <div className="text-xs text-muted-foreground">Overdue</div>
                  </div>
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-muted-foreground text-sm">Loading compliance data...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="text-red-600">⚠️</div>
                    <div>
                      <p className="text-red-800 font-medium">Error loading data</p>
                      <p className="text-red-600 text-sm">{error}</p>
                      <button 
                        onClick={loadComplianceReports}
                        className="text-red-700 underline text-sm mt-1"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              ) : complianceData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {complianceData.map((item) => (
                    <ComplianceOverviewCard
                      key={item.id}
                      title={item.title}
                      status={item.status}
                      lastUpdated={item.lastUpdated}
                      dueDate={item.dueDate}
                      description={item.description}
                      icon={item.icon}
                      onViewDetails={() => handleViewDetails(item.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No compliance reports found</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button variant="outline" className="h-20 flex-col gap-2">
                  <Icon name="FileText" size={24} />
                  <span className="text-sm">Generate Report</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2">
                  <Icon name="Upload" size={24} />
                  <span className="text-sm">Upload Document</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2">
                  <Icon name="Calendar" size={24} />
                  <span className="text-sm">View Calendar</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2">
                  <Icon name="AlertTriangle" size={24} />
                  <span className="text-sm">Risk Assessment</span>
                </Button>
              </div>
            </div>
          </div>
        );
      case 'reports':
        return <ReportGenerationTool />;
      case 'calendar':
        return <ComplianceCalendar />;
      case 'documents':
        return <DocumentManagement />;
      case 'risks':
        return <RiskAssessmentDashboard />;
      case 'notifications':
        return <NotificationSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Compliance & Statutory Reports - FacilityPro ERP</title>
        <meta name="description" content="Manage statutory compliance requirements, generate reports, and monitor regulatory deadlines for facility management operations." />
      </Helmet>

      <HeaderNavigation
        onSidebarToggle={handleSidebarToggle}
        user={userProfile || user}
        notifications={[]}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />
          
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Compliance & Statutory Reports</h1>
                <p className="text-muted-foreground mt-2">
                  Ensure adherence to Indian statutory requirements and generate mandatory compliance reports
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Export Data
                </Button>
                <Button variant="default" iconName="Plus" iconPosition="left">
                  New Compliance Item
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ComplianceStatutoryReports;